import { getMediaUrl } from '@internal/utils/media/getMediaSrc';
import { CmsClickEventTargetName } from '@internal/utils/tracking/TTrackingEvent';
import { tracker } from '@internal/utils/tracking/tracker';
import {
    ArrowBack,
    ArrowForward,
    KeyboardArrowDown,
    KeyboardArrowUp,
} from '@mui/icons-material';
import { Text } from '@renderer-ui-library/atoms';
import {
    CmsMediaFileData,
    TLinkToCategoryPageData,
    TLinkToPageData,
    TLinkToSystemPageData,
} from '@renderer-ui-library/molecules';
import {
    Button,
    Collapse,
    IconButton,
    Typography,
} from '@renderer-ui-library/mui';
import classNames from 'classnames';
import React, { useCallback, useState } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import styles from './cardBannerBlock.module.scss';
import { CardTrackingType } from './ICardBannerBlockData';

const clickEvents: Record<CardTrackingType, CmsClickEventTargetName> = {
    [CardTrackingType.HowToBuyOnline]: 'block.service_cards.how_to_buy_online',
    [CardTrackingType.Delivery]: 'block.service_cards.delivery',
    [CardTrackingType.SafetyInspection]:
        'block.service_cards.safety_inspection',
    [CardTrackingType.SaveWallet]: 'block.service_cards.save_wallet',
    [CardTrackingType.SecuredBeyondPurchase]:
        'block.service_cards.secured_beyond_purchase',
    [CardTrackingType.WithAdviceToMachine]:
        'block.service_cards.with_advice_to_machine',
    [CardTrackingType.NoFees]: 'block.service_cards.no_fees',
    [CardTrackingType.AuctionTransport]:
        'block.service_cards.auction_transport',
    [CardTrackingType.HighQuality]: 'block.service_cards.high_quality',
};

type Card = {
    headline: string;
    description: string;
    systemPage: null | TLinkToSystemPageData;
    page: null | TLinkToPageData;
    machineCategoryPage: null | TLinkToCategoryPageData;
    image: CmsMediaFileData;
    trackingCardType: CardTrackingType;
};

type Props = {
    headline: string;
    image: CmsMediaFileData;
    cards: Card[];
    border?: boolean;
    elevated?: boolean;
    initialOpen?: boolean;
    collapsible?: boolean;
};

export const Cards: React.FC<Props> = React.memo(
    ({
        headline,
        image,
        cards,
        border,
        elevated,
        initialOpen,
        collapsible = true,
    }) => {
        const [isOpen, setOpen] = useState(initialOpen ?? false);
        const [activeIndex, setActiveIndex] = useState(0);

        const trackSlideChange = useCallback(
            (newActiveIndex: number) => {
                const card = cards[newActiveIndex];
                if (card) {
                    tracker.trackEvent({
                        name: 'click',
                        target_name: clickEvents[card.trackingCardType],
                        is_interaction: true,
                    });
                }
            },
            [cards],
        );

        const handleExpandButtonClick = useCallback(() => {
            tracker.trackEvent({
                name: 'click',
                target_name: 'block.service_cards',
                is_interaction: true,
            });

            setOpen(isCurrentlyOpen => {
                if (!isCurrentlyOpen) {
                    const card = cards[activeIndex];
                    if (card) {
                        tracker.trackEvent({
                            name: 'click',
                            target_name:
                                clickEvents[
                                    cards[activeIndex].trackingCardType
                                ],
                            is_interaction: true,
                        });
                    }
                }

                return !isCurrentlyOpen;
            });
        }, [activeIndex, cards]);

        const handleSlideChange = useCallback(
            (swiper: SwiperType) => {
                setActiveIndex(swiper.activeIndex);
                trackSlideChange(swiper.activeIndex);
            },
            [trackSlideChange],
        );

        return (
            <div
                className={classNames(styles.cardBannerContainer, {
                    [styles.hideAnimation]: isOpen,
                    [styles.border]: border,
                    [styles.elevated]: elevated,
                })}
            >
                <div className={classNames({ [styles.hide]: isOpen })}>
                    <Button
                        onClick={handleExpandButtonClick}
                        className={styles.button}
                        disableRipple
                    >
                        {image.data && (
                            <img
                                className={classNames({
                                    [styles.hide]: isOpen,
                                })}
                                loading="lazy"
                                alt={image.data.attributes.alternativeText}
                                src={getMediaUrl(image.data, {
                                    width: 60,
                                })}
                                width="60"
                            />
                        )}
                        <Typography
                            variant="h6"
                            className={classNames({ [styles.hide]: isOpen })}
                            paddingLeft={2}
                        >
                            {headline}
                        </Typography>

                        <KeyboardArrowDown
                            fontSize="large"
                            className={classNames(styles.arrowIcon)}
                        />
                    </Button>
                </div>
                <Collapse in={isOpen}>
                    <Swiper
                        modules={[Pagination, Navigation]}
                        pagination={{ clickable: true, dynamicBullets: true }}
                        navigation={{
                            nextEl: `.${styles.nextRef}`,
                            prevEl: `.${styles.prevRef}`,
                        }}
                        rewind
                        speed={0}
                        onSlideChange={handleSlideChange}
                    >
                        {collapsible && (
                            <IconButton
                                onClick={handleExpandButtonClick}
                                color="primary"
                                disableRipple
                                className={classNames(
                                    styles.collapseButton,
                                    styles.arrowIcon,
                                    {
                                        [styles.hide]: !isOpen,
                                    },
                                )}
                                style={{
                                    position: 'absolute',
                                    paddingRight: '12px',
                                }}
                            >
                                <KeyboardArrowUp fontSize="medium" />
                            </IconButton>
                        )}
                        {cards.map(({ headline, description, image }, i) => {
                            const isActiveSlide = i === activeIndex;

                            return (
                                <SwiperSlide
                                    key={image.data?.id}
                                    className={classNames(styles.swiperSlide, {
                                        [styles.active]: isActiveSlide,
                                    })}
                                >
                                    {image.data && (
                                        <img
                                            loading="lazy"
                                            alt={
                                                image.data.attributes
                                                    .alternativeText ?? ''
                                            }
                                            src={getMediaUrl(image.data, {
                                                width: 120,
                                            })}
                                            width="120"
                                        />
                                    )}
                                    <div
                                        className={classNames(
                                            styles.cardTextContentWrapper,
                                            {
                                                [styles.textActive]:
                                                    isActiveSlide,
                                            },
                                        )}
                                    >
                                        <Typography variant="h4">
                                            {headline}
                                        </Typography>
                                        <Text text={description} />
                                    </div>
                                </SwiperSlide>
                            );
                        })}
                        <div className={styles.navigationContainer}>
                            <div className={styles.slidesIndexWrapper}>
                                <Typography variant="body2">
                                    {activeIndex + 1} / {cards.length}
                                </Typography>
                            </div>
                            <IconButton className={styles.prevRef}>
                                <ArrowBack />
                            </IconButton>
                            <IconButton className={styles.nextRef}>
                                <ArrowForward />
                            </IconButton>
                        </div>
                    </Swiper>
                </Collapse>
            </div>
        );
    },
);

Cards.displayName = 'Cards';
